import React, { useState, useEffect } from "react"
import { subscribeEvents } from "../../../utils/eventHelpers"
import Icon from "../../../components/AppIcon"

const EVENT_TYPES = [
  "CREDENTIAL_ISSUED", "CREDENTIAL_REVOKED", "CREDENTIAL_REQUEST_APPROVED", "CREDENTIAL_REQUEST_REJECTED",
  "PAYMENT_COMPLETED", "PAYMENT_REFUNDED", "PAYMENT_FAILED", "ORDER_CREATED", "ORDER_STATUS_CHANGED",
  "ORGANIZATION_CREATED", "ORGANIZATION_SUSPENDED", "ORGANIZATION_REINSTATED", "ORG_VERIFICATION_REQUESTED",
  "MEMBER_INVITED", "MEMBER_ROLE_CHANGED", "MEMBER_REMOVED",
  "MENTOR_DESIGNATED", "MENTOR_REMOVED", "MENTORSHIP_CREATED", "MENTORSHIP_ENDED",
  "COURSE_CREATED", "COURSE_PUBLISHED", "COURSE_DELETED", "COURSE_COMPLETED",
  "PROGRAM_CREATED", "PROGRAM_DELETED",
  "LIVE_CLASS_SCHEDULED", "LIVE_CLASS_ENDED",
  "INVITATION_SENT", "INVITATION_ACCEPTED", "INVITATION_REVOKED",
  "ENROLLMENT_CREATED",
]

const SEVERITY_STYLES = {
  critical: { dot: "bg-destructive", text: "text-destructive", label: "Critical" },
  warning: { dot: "bg-warning", text: "text-warning", label: "Warning" },
  info: { dot: "bg-success", text: "text-muted-foreground", label: "Info" },
}

const formatEventType = (type) =>
  (type || "").split("_").map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(" ")

const formatTimestamp = (ts) => {
  const ms = ts?.toMillis ? ts.toMillis() : null
  return ms ? new Date(ms).toLocaleString() : "—"
}

const EventExplorer = ({ onNavigateToOrg }) => {
  const [typeFilter, setTypeFilter] = useState("")
  const [severityFilter, setSeverityFilter] = useState("")
  const [orgIdFilter, setOrgIdFilter] = useState("")
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedEvent, setSelectedEvent] = useState(null)

  useEffect(() => {
    setLoading(true)
    const unsubscribe = subscribeEvents(
      { type: typeFilter || undefined, severity: severityFilter || undefined, orgId: orgIdFilter?.trim() || undefined },
      (rows) => {
        setEvents(rows)
        setLoading(false)
      }
    )
    return () => unsubscribe()
  }, [typeFilter, severityFilter, orgIdFilter])

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="text-sm bg-card border border-border rounded-lg px-3 py-2 text-foreground"
        >
          <option value="">All event types</option>
          {EVENT_TYPES.map((t) => (
            <option key={t} value={t}>{formatEventType(t)}</option>
          ))}
        </select>

        <select
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value)}
          className="text-sm bg-card border border-border rounded-lg px-3 py-2 text-foreground"
        >
          <option value="">All severities</option>
          <option value="critical">Critical</option>
          <option value="warning">Warning</option>
          <option value="info">Info</option>
        </select>

        <input
          type="text"
          placeholder="Filter by Org ID..."
          value={orgIdFilter}
          onChange={(e) => setOrgIdFilter(e.target.value)}
          className="text-sm bg-card border border-border rounded-lg px-3 py-2 text-foreground placeholder:text-muted-foreground"
        />

        <span className="flex items-center gap-1.5 text-xs text-muted-foreground ml-auto">
          <span className="size-1.5 rounded-full bg-success animate-pulse" />
          Live
        </span>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden font-mono text-sm">
        <div className="hidden sm:grid grid-cols-[auto_1fr_1fr_auto] gap-3 px-4 py-2 border-b border-border text-xs text-muted-foreground uppercase tracking-wide">
          <span></span>
          <span>Event</span>
          <span>Organization</span>
          <span>Time</span>
        </div>

        {loading ? (
          <div className="p-6 text-center text-muted-foreground text-sm">Loading events...</div>
        ) : events?.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground text-sm">No events match these filters.</div>
        ) : (
          <div className="divide-y divide-border">
            {events?.map((event) => {
              const style = SEVERITY_STYLES[event?.severity] || SEVERITY_STYLES.info
              return (
                <button
                  key={event?.id}
                  onClick={() => setSelectedEvent(event)}
                  className="w-full grid grid-cols-[auto_1fr] sm:grid-cols-[auto_1fr_1fr_auto] gap-3 px-4 py-2.5 hover:bg-muted/50 transition-colors text-left items-center"
                >
                  <span className={`size-2 rounded-full shrink-0 ${style.dot}`} />
                  <span className="text-foreground truncate">{formatEventType(event?.type)}</span>
                  <span className="hidden sm:block text-muted-foreground truncate text-xs">
                    {event?.orgId || "—"}
                  </span>
                  <span className="hidden sm:block text-muted-foreground text-xs shrink-0">
                    {formatTimestamp(event?.createdAt)}
                  </span>
                </button>
              )
            })}
          </div>
        )}
      </div>

      {selectedEvent && (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setSelectedEvent(null)}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative w-full max-w-md bg-card border-l border-border h-full overflow-y-auto p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-foreground">Event Details</h3>
              <button onClick={() => setSelectedEvent(null)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="space-y-3 font-mono text-sm">
              <div className="flex items-center gap-2">
                <span className={`size-2 rounded-full ${(SEVERITY_STYLES[selectedEvent?.severity] || SEVERITY_STYLES.info).dot}`} />
                <span className="text-foreground font-semibold">{formatEventType(selectedEvent?.type)}</span>
              </div>

              <DetailRow label="Event ID" value={selectedEvent?.eventId || selectedEvent?.id} />
              <DetailRow label="Severity" value={(SEVERITY_STYLES[selectedEvent?.severity] || SEVERITY_STYLES.info).label} />
              <DetailRow label="Actor" value={selectedEvent?.actorUid || "System"} />
              <DetailRow label="Actor Type" value={selectedEvent?.actorType || "—"} />
              <DetailRow label="Organization" value={selectedEvent?.orgId || "—"} />
              <DetailRow label="Target" value={selectedEvent?.targetPath || "—"} />
              <DetailRow label="Timestamp" value={formatTimestamp(selectedEvent?.createdAt)} />

              {selectedEvent?.metadata && Object.keys(selectedEvent.metadata).length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Metadata</p>
                  <pre className="bg-muted rounded-lg p-3 text-xs overflow-x-auto text-foreground">
                    {JSON.stringify(selectedEvent.metadata, null, 2)}
                  </pre>
                </div>
              )}

              {selectedEvent?.orgId && onNavigateToOrg && (
                <button
                  onClick={() => onNavigateToOrg(selectedEvent.orgId)}
                  className="w-full mt-2 text-sm px-3 py-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90"
                >
                  View Organization →
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const DetailRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3">
    <span className="text-xs text-muted-foreground shrink-0">{label}</span>
    <span className="text-foreground text-right break-all">{value}</span>
  </div>
)

export default EventExplorer